/**
 * PDP promo pricing
 * Reads the promo configs rendered by selector-pdp.liquid (#pdp-promo-config, one entry per theme-settings
 * promo slot) and shows the discounted price on every eligible plan while one of the codes is on the cart.
 * - Arriving with ?discount=CODE applies the code to the cart, then strips the param.
 * - Source of truth afterwards is the cart's discount codes (/cart.js).
 * - Cart requests made by anything on the page (cart drawer app included) are watched, so removing
 *   the code in the drawer reverts the PDP prices immediately, no refresh needed.
 * Display only: the actual discount is still applied by Shopify at checkout.
 *
 * UI copy comes from window.jwStrings.selectorPdp (rendered by selector-pdp.liquid from locale keys),
 * with the English text as fallback. Placeholders use the [name] format.
 */
const PDPStrings = (() => {
  function get(key, fallback, vars) {
    const strings = (window.jwStrings && window.jwStrings.selectorPdp) || {};
    const template = typeof strings[key] === 'string' && strings[key] ? strings[key] : fallback;

    return template.replace(/\[(\w+)\]/g, (match, name) =>
      vars && Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : match
    );
  }

  // Comma-separated locale value as a trimmed, non-empty list
  function list(key, fallback) {
    return get(key, fallback).split(',').map(item => item.trim()).filter(Boolean);
  }

  return { get, list };
})();

// Storefront URL with the current locale prefix (no-op on the primary language)
function pdpLocalizeUrl(url) {
  return typeof window.jwLocalizeUrl === 'function' ? window.jwLocalizeUrl(url) : url;
}

const PDPPromo = (() => {
  const CONFIG_ID = 'pdp-promo-config';
  const COOKIE_NAME = 'discount_code';

  function readCookie(name) {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    if (!match) return '';
    try {
      return decodeURIComponent(match[1]);
    } catch (e) {
      return match[1];
    }
  }

  function formatMoney(cents) {
    const dollars = cents / 100;
    const whole = Number.isInteger(dollars);
    return '$' + dollars.toLocaleString('en-US', {
      minimumFractionDigits: whole ? 0 : 2,
      maximumFractionDigits: whole ? 0 : 2
    });
  }

  // Returns [{ codes: [..], amount (cents), recurring: 'first' | 'limited' | 'all', payments, handles: Set }]
  function getPromos() {
    const el = document.getElementById(CONFIG_ID);
    if (!el) return [];

    let list;
    try {
      list = JSON.parse(el.textContent);
    } catch (e) {
      return [];
    }

    if (!Array.isArray(list)) list = [list];

    return list
      .map(p => ({
        codes: (p.codes || []).map(c => String(c).trim().toUpperCase()).filter(Boolean),
        amount: Math.round(Number(p.amount)),
        recurring: ['all', 'limited'].includes(p.recurring) ? p.recurring : 'first',
        payments: Math.max(1, Math.round(Number(p.payments) || 1)),
        ...parseHandles(p.handles)
      }))
      .filter(p => p.codes.length && p.amount > 0 && p.handles.size);
  }

  // Handle lines are "handle" or "handle:renewal" where renewal is the discounted price (in $) charged
  // on each renewal after the first order, e.g. "tirzepatide-program-three-months:697"
  function parseHandles(lines) {
    const handles = new Set();
    const renewals = new Map();

    (lines || []).forEach(line => {
      const [rawHandle, rawRenewal] = String(line).split(':');
      const handle = String(rawHandle || '').trim().toLowerCase();
      if (!handle) return;

      handles.add(handle);

      const renewal = Number(rawRenewal);
      if (renewal > 0) renewals.set(handle, Math.round(renewal * 100));
    });

    return { handles, renewals };
  }

  // First promo/code pair matching one of the given codes, or null
  function match(promos, codes) {
    for (const code of codes) {
      const promo = promos.find(p => p.codes.includes(code));
      if (promo) return { ...promo, code };
    }
    return null;
  }

  function stripDiscountParam() {
    try {
      const url = new URL(window.location.href);
      if (!url.searchParams.has('discount')) return;
      url.searchParams.delete('discount');
      window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
    } catch (e) {}
  }

  async function getCartCodes(fetchImpl = window.fetch) {
    const res = await fetchImpl(pdpLocalizeUrl('/cart.js'), { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('cart.js ' + res.status);
    const cart = await res.json();
    return (cart.discount_codes || []).map(d => String(d.code || '').trim().toUpperCase());
  }

  // Puts the code on the cart and returns the cart's resulting discount codes
  async function applyCodeToCart(code) {
    const res = await fetch(pdpLocalizeUrl('/cart/update.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: code })
    });
    if (!res.ok) throw new Error('cart/update.js ' + res.status);
    const cart = await res.json();
    return (cart.discount_codes || []).map(d => String(d.code || '').trim().toUpperCase());
  }

  // Resolves to the active promo ({ ...promo, code }) or null
  async function detect(promos) {
    const urlCode = (new URLSearchParams(window.location.search).get('discount') || '').trim().toUpperCase();

    // Fresh arrival with ?discount=CODE: put it on the cart, then drop the param so a refresh can't re-add it
    if (urlCode) {
      stripDiscountParam();

      const fromUrl = match(promos, [urlCode]);
      if (fromUrl) {
        try {
          return match(promos, await applyCodeToCart(urlCode));
        } catch (e) {
          return fromUrl;
        }
      }
    }

    try {
      return match(promos, await getCartCodes());
    } catch (e) {
      // cart.js unavailable: fall back to Shopify's discount cookie
      return match(promos, [readCookie(COOKIE_NAME).trim().toUpperCase()]);
    }
  }

  // --- Cart watcher -------------------------------------------------------
  // Listens to every cart request on the page (fetch + XHR, Ajax API + Storefront GraphQL) and reports
  // the cart's discount codes whenever they can be read, re-fetching /cart.js after mutations that
  // don't return them.

  const CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?|\/discount\//;
  const CART_READ = /\/cart(\.js)?(\?|$)/;
  const GRAPHQL = /\/api\/[^/]+\/graphql(\.json)?/;

  function urlPath(url) {
    try {
      return new URL(url, window.location.origin).pathname;
    } catch (e) {
      return String(url || '');
    }
  }

  function isCartRequest(url) {
    const path = urlPath(url);
    return CART_MUTATION.test(path) || CART_READ.test(path) || GRAPHQL.test(path);
  }

  // Extract discount codes from a cart-ish JSON payload, or null if it doesn't carry them
  function codesFromPayload(data) {
    if (!data || typeof data !== 'object') return null;

    if (Array.isArray(data.discount_codes)) {
      return data.discount_codes.map(d => String(d.code || '').trim().toUpperCase());
    }

    // Storefront API: look for any { discountCodes: [{ code }] } in the response
    let found = null;
    const walk = (node, depth) => {
      if (found || !node || typeof node !== 'object' || depth > 6) return;
      if (Array.isArray(node.discountCodes)) {
        found = node.discountCodes.map(d => String((d && d.code) || '').trim().toUpperCase());
        return;
      }
      Object.keys(node).forEach(k => walk(node[k], depth + 1));
    };
    walk(data, 0);
    return found;
  }

  function watchCart(onCodes) {
    const originalFetch = window.fetch;
    let refetchTimer = null;
    let selfRequest = false;

    function scheduleRefetch() {
      clearTimeout(refetchTimer);
      refetchTimer = setTimeout(async () => {
        selfRequest = true;
        try {
          onCodes(await getCartCodes(originalFetch));
        } catch (e) {
        } finally {
          selfRequest = false;
        }
      }, 400);
    }

    function handle(url, text) {
      if (!isCartRequest(url)) return;

      let data = null;
      try {
        data = JSON.parse(text);
      } catch (e) {}

      const codes = codesFromPayload(data);

      if (codes) {
        onCodes(codes);
      } else if (CART_MUTATION.test(urlPath(url)) || GRAPHQL.test(urlPath(url))) {
        scheduleRefetch();
      }
    }

    window.fetch = function (input, init) {
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      const promise = originalFetch.apply(this, arguments);

      if (!selfRequest && isCartRequest(url)) {
        promise.then(res => {
          res.clone().text().then(text => handle(url, text)).catch(() => {});
        }).catch(() => {});
      }

      return promise;
    };

    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url) {
      this.__pdpPromoUrl = url;
      return originalOpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function () {
      const url = this.__pdpPromoUrl;
      if (url && isCartRequest(url)) {
        this.addEventListener('load', () => {
          try {
            if (this.responseType === '' || this.responseType === 'text') {
              handle(url, this.responseText);
            } else if (this.responseType === 'json') {
              handle(url, JSON.stringify(this.response));
            }
          } catch (e) {}
        });
      }
      return originalSend.apply(this, arguments);
    };

    // Back/forward cache restores skip DOMContentLoaded: re-check the cart
    window.addEventListener('pageshow', e => {
      if (e.persisted) scheduleRefetch();
    });
  }

  // --- Rendering ----------------------------------------------------------

  const SEL_PRIMARY = '.pdp-payment-option-top-right-price-primary, .pdp-payment-option-content-price-primary';
  const SEL_DAILY = '.pdp-payment-option-content-price-badge-value';

  // Save the server-rendered values once so the promo can be reverted
  function snapshot(plan = HTMLElement.prototype) {
    if (plan.__pdpPromoOriginal) return;

    plan.__pdpPromoOriginal = {
      primary: Array.from(plan.querySelectorAll(SEL_PRIMARY)).map(el => el.textContent),
      daily: (plan.querySelector(SEL_DAILY) || {}).textContent,
      regularPrice: plan.getAttribute('data-product-regular-price'),
      affirmPrice: plan.getAttribute('data-product-affirm-price')
    };
  }

  function revert(plan = HTMLElement.prototype) {
    const original = plan.__pdpPromoOriginal;
    if (!original) return;

    plan.querySelectorAll(SEL_PRIMARY).forEach((el, i) => {
      el.textContent = original.primary[i];
    });

    const daily = plan.querySelector(SEL_DAILY);
    if (daily && original.daily !== undefined) daily.textContent = original.daily;

    plan.querySelectorAll('.pdp-promo-old').forEach(el => {
      el.textContent = '';
      el.hidden = true;
    });

    const note = plan.querySelector('.pdp-promo-note');
    if (note) {
      note.textContent = '';
      note.hidden = true;
    }

    plan.setAttribute('data-product-regular-price', original.regularPrice);
    plan.setAttribute('data-product-affirm-price', original.affirmPrice);
    plan.classList.remove('promo-applied');
  }

  function isEligible(plan, promo, affirm = false) {
    const handle = (plan.getAttribute(affirm ? 'data-promo-affirm-handle' : 'data-promo-handle') || '').trim().toLowerCase();
    return Boolean(promo && handle && promo.handles.has(handle));
  }

  // Returns true when the discount was applied to this plan
  function apply(plan = HTMLElement.prototype, config) {
    snapshot(plan);
    revert(plan);

    if (!isEligible(plan, config)) return false;

    const totalCents = Number(plan.getAttribute('data-plan-price-cents'));
    const months = Number(plan.getAttribute('data-plan-months')) || 1;

    if (!(totalCents > config.amount)) return false;

    const discountedCents = totalCents - config.amount;
    const perMonthCents = Math.round(discountedCents / months);
    let regularPerMonthText = '';

    plan.querySelectorAll(SEL_PRIMARY).forEach(el => {
      const oldText = el.textContent.trim();
      const old = el.parentElement.querySelector('.pdp-promo-old');

      regularPerMonthText = regularPerMonthText || oldText;

      if (old) {
        old.textContent = oldText;
        old.hidden = false;
      }

      el.textContent = formatMoney(perMonthCents);
    });

    const daily = plan.querySelector(SEL_DAILY);
    if (daily) {
      daily.textContent = '$ ' + (discountedCents / 100 / (months * 31)).toFixed(2);
    }

    const note = plan.querySelector('.pdp-promo-note');
    if (note) {
      const handle = (plan.getAttribute('data-promo-handle') || '').trim().toLowerCase();
      const renewalCents = config.renewals && config.renewals.get(handle);

      if (config.recurring !== 'first' && renewalCents) {
        const renewalPerMonth = formatMoney(Math.floor(renewalCents / months / 100) * 100);
        note.textContent = PDPStrings.get('promoNoteRenewal', '[code] applied: [first_price] first month, then [renewal_price] / month', {
          code: config.code,
          first_price: formatMoney(perMonthCents),
          renewal_price: renewalPerMonth
        });
      } else if (config.recurring === 'all') {
        note.textContent = PDPStrings.get('promoNoteAll', '[code] applied: same price on every renewal', { code: config.code });
      } else if (config.recurring === 'limited' && config.payments > 1) {
        note.textContent = PDPStrings.get('promoNoteLimited', '[code] applied: same price for your first [payments] payments, then [regular_price] / month', {
          code: config.code,
          payments: config.payments,
          regular_price: regularPerMonthText
        });
      } else {
        const period = months === 1
          ? PDPStrings.get('promoPeriodFirstMonth', 'first month')
          : PDPStrings.get('promoPeriodFirstMonths', 'first [months] months', { months });
        note.textContent = PDPStrings.get('promoNoteFirst', '[code] applied: [period], then [regular_price] / month', {
          code: config.code,
          period,
          regular_price: regularPerMonthText
        });
      }
      note.hidden = false;
    }

    plan.setAttribute('data-product-regular-price', String(discountedCents));

    if (isEligible(plan, config, true)) {
      plan.setAttribute('data-product-affirm-price', String(discountedCents));
    }

    plan.classList.add('promo-applied');

    return true;
  }

  function refreshAffirm() {
    const affirm = window.affirm;
    if (affirm && affirm.ui && typeof affirm.ui.ready === 'function') {
      affirm.ui.ready(() => {
        if (typeof affirm.ui.refresh === 'function') affirm.ui.refresh();
      });
    }
  }

  return { getPromos, match, detect, watchCart, apply, isEligible, refreshAffirm };
})();

function v2() {
  const ROOT_PLAN = '.pdp-payment-option';
  const ROOT_PLAN_RADIO = '.pdp-payment-option-top-left-option';
  const ROOT_CHECKOUT_BUTTONS_CONTAINER = '.pdp-payments-actions-buttons';
  const ROOT_CHECKOUT_BUTTONS = '.pdp-payments-actions-button';
  const ROOT_AFFIRM_MESSAGE = '.affirm-as-low-as[data-amount]';
  const CLASSNAME_PLAN_ACTIVE = 'active';
  const CLASSNAME_BUTTON_AFFIRM = 'affirm';
  const CLASSNAME_BUTTON_REGULAR = 'regular';
  const ATTR_AFFIRM_PRICE = 'data-product-affirm-price';
  const ATTR_AFFIRM_JSON = 'data-product-affirm-json';
  const ATTR_REGULAR_JSON = 'data-product-regular-json';
  const ATTR_AFFIRM_CHECKOUT = 'data-product-affirm-checkout';
  const ATTR_REGULAR_CHECKOUT = 'data-product-regular-checkout';

  const plans = document.querySelectorAll(ROOT_PLAN);
  const plansRadios = document.querySelectorAll(ROOT_PLAN_RADIO);
  const checkoutButtonsContainer = document.querySelector(ROOT_CHECKOUT_BUTTONS_CONTAINER);
  const affirmMessage = document.querySelector(ROOT_AFFIRM_MESSAGE);

  const promos = PDPPromo.getPromos();
  let activePromo = null;

  const escapeRegExp = text => String(text).replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');

  // Plan titles are merchant content and get translated, so the month unit words ("Month,Months" in
  // English) and the compact form ("[months]-Month") come from the current locale instead of an
  // English-only pattern.
  const PLAN_MONTH_UNITS = PDPStrings.list('planTitleMonthUnits', 'Month,Months').map(unit => unit.toLowerCase());

  // Per-month price suffixes stripped from the page title ("/mo" in English). The English unit is
  // always kept since an untranslated SEO title still carries it.
  const TITLE_PRICE_UNITS = Array.from(new Set(['mo'].concat(PDPStrings.list('titlePriceUnits', 'mo'))));
  const TITLE_PRICE_PATTERN = new RegExp('\\s*-\\s*\\$?\\s*[\\d.,]+\\s*\\/(?:' + TITLE_PRICE_UNITS.map(escapeRegExp).join('|') + ')', 'i');

  function updatePlanTitlePage(planText) {
    const originalTitle = document.title.split('|');

    const formatPlan = (text) => {
      if (!text) return '';
      let compacted = false;

      // First "<number> <month unit>" only, e.g. "6 Month Plan" -> "6-Month Plan" in English
      return text.trim().toLowerCase().split(/\s+/).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ').replace(/(\d+)\s+(\p{L}+)/gu, (match, months, unit) => {
        if (compacted || !PLAN_MONTH_UNITS.includes(unit.toLowerCase())) return match;
        compacted = true;
        return PDPStrings.get('planTitleMonths', '[months]-Month', { months, unit });
      });
    };

    const formattedPlanText = formatPlan(planText);

    // Clear any previous price (e.g. " - $49/mo" or "- $ 49 /mo") from the main title
    // This prevents modifying other parts of the title if it contains hyphens (e.g. "GLP-1")
    let cleanMainTitle = originalTitle[0].replace(TITLE_PRICE_PATTERN, '').trim();

    // Clear any previously appended plan texts so we don't duplicate them
    const allPlanTitles = Array.from(document.querySelectorAll('.pdp-payment-option-top-left-title')).map(el => formatPlan(el.textContent));

    allPlanTitles.forEach(title => {
      if (title) {
        cleanMainTitle = cleanMainTitle.replace(new RegExp('\\s*' + escapeRegExp(title), 'gi'), '');
      }
    });

    cleanMainTitle = cleanMainTitle.trim();

    // Assign the new format appending the plan text
    originalTitle[0] = `${cleanMainTitle} ${formattedPlanText} `;

    document.title = originalTitle.join('|');
  }

  function planSet(plan = HTMLElement.prototype, updateTitle = false) {
    plans.forEach(plan => plan.classList.remove(CLASSNAME_PLAN_ACTIVE));
    plan.classList.add(CLASSNAME_PLAN_ACTIVE);

    const planTitleEl = plan.querySelector('.pdp-payment-option-top-left-title');
    if (planTitleEl && updateTitle) {
      updatePlanTitlePage(planTitleEl.textContent);
    }

    const checkoutAffirm = plan.getAttribute(ATTR_AFFIRM_CHECKOUT);
    const checkoutRegular = plan.getAttribute(ATTR_REGULAR_CHECKOUT);

    affirmMessage.classList.remove(CLASSNAME_PLAN_ACTIVE);
    affirmMessage.setAttribute('data-amount', '0');
    checkoutButtonsContainer.classList.remove(CLASSNAME_BUTTON_AFFIRM, CLASSNAME_BUTTON_REGULAR);
    document.querySelectorAll(ROOT_CHECKOUT_BUTTONS).forEach((button = HTMLAnchorElement.prototype) => {
      button.removeAttribute('href');
    });

    if (checkoutAffirm !== 'undefined') {
      affirmMessage.classList.add(CLASSNAME_PLAN_ACTIVE);
      checkoutButtonsContainer.classList.add(CLASSNAME_BUTTON_AFFIRM);
      const button = document.querySelector(`${ROOT_CHECKOUT_BUTTONS}.affirm`);

      affirmMessage.setAttribute('data-amount', plan.getAttribute(ATTR_AFFIRM_PRICE));

      button.setAttribute('href', checkoutAffirm);
    }

    if (checkoutRegular !== 'undefined') {
      checkoutButtonsContainer.classList.add(CLASSNAME_BUTTON_REGULAR);
      const button = document.querySelector(`${ROOT_CHECKOUT_BUTTONS}.regular`);
      button.setAttribute('href', checkoutRegular);
    }

    if (promos.length) {
      PDPPromo.refreshAffirm();
    }
  }

  function generateCheckout(variantId = String.prototype, sellingPlanId = String.prototype, discountCode = '') {
    if (!variantId) {
      console.error("Error: Variant ID are required.");
      return;
    }

    // Every hop of the permalink is localized before encoding so the shopper stays on their language
    const checkoutPath = discountCode
      ? encodeURIComponent(pdpLocalizeUrl(`/checkout?discount=${encodeURIComponent(discountCode)}`))
      : pdpLocalizeUrl('/checkout');
    const addParams = `items[][id]=${variantId}&items[][quantity]=1&items[][selling_plan]=${sellingPlanId}&return_to=${checkoutPath}`;
    const addUrl = `${pdpLocalizeUrl('/cart/add')}?${addParams}`;
    const finalUrl = `${pdpLocalizeUrl('/cart/clear')}?return_to=${encodeURIComponent(addUrl)}`;

    return finalUrl;
  }

  function getCheckoutValues(json = String.prototype) {
    const variant = JSON.parse(json);

    const data = {
      variantId: "",
      sellingPlan: ""
    };

    if (!variant) return data;

    if (variant.id) {
      data.variantId = variant.id;
    }

    if (variant.requires_selling_plan === true) {
      if (
        !variant.selling_plan_allocations ||
        !variant.selling_plan_allocations[0] ||
        !variant.selling_plan_allocations[0].selling_plan_id
      ) return data;

      data.sellingPlan = variant.selling_plan_allocations[0].selling_plan_id;
    }

    return data;
  }

  // Applies/reverts the promo on a plan and rebuilds its checkout links
  function planRender(plan = HTMLElement.prototype) {
    const jsonAffirm = plan.getAttribute(ATTR_AFFIRM_JSON);
    const jsonRegular = plan.getAttribute(ATTR_REGULAR_JSON);
    const promoApplied = PDPPromo.apply(plan, activePromo);
    const regularCode = promoApplied ? activePromo.code : '';
    const affirmCode = promoApplied && PDPPromo.isEligible(plan, activePromo, true) ? activePromo.code : '';

    if (jsonAffirm) {
      const { variantId, sellingPlan } = getCheckoutValues(jsonAffirm);
      plan.setAttribute(ATTR_AFFIRM_CHECKOUT, generateCheckout(variantId, sellingPlan, affirmCode));
    }

    if (jsonRegular) {
      const { variantId, sellingPlan } = getCheckoutValues(jsonRegular);
      plan.setAttribute(ATTR_REGULAR_CHECKOUT, generateCheckout(variantId, sellingPlan, regularCode));
    }
  }

  function renderAll() {
    plans.forEach(planRender);

    const active = Array.from(plans).find(plan => plan.classList.contains(CLASSNAME_PLAN_ACTIVE));
    if (active) planSet(active);
  }

  function setActivePromo(promo) {
    const next = promo || null;
    if ((next && next.code) === (activePromo && activePromo.code)) return;
    activePromo = next;
    renderAll();
  }

  plansRadios.forEach(radio => {
    radio.addEventListener('change', e => {
      if (!e.target) return;

      const plan = e.target.closest(ROOT_PLAN);

      if (!plan) return;

      planSet(plan, true);
    });
  });

  // Initial render at regular prices so the buttons work immediately
  renderAll();

  if (promos.length) {
    // Keep the PDP in sync with the cart (e.g. code removed or swapped in the cart drawer)
    PDPPromo.watchCart(codes => setActivePromo(PDPPromo.match(promos, codes)));

    PDPPromo.detect(promos)
      .then(setActivePromo)
      .catch(() => {});
  }
}

document.addEventListener('DOMContentLoaded', () => {
  v2();
});
